import { useState, useEffect } from 'react';
import { X, AlertTriangle, Trash2, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const DuplicatePhotosDialog = ({ open, onClose, galleryId, onPhotosRemoved }) => {
  const [duplicates, setDuplicates] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [removing, setRemoving] = useState(false);
  const [totalDuplicatePhotos, setTotalDuplicatePhotos] = useState(0);

  useEffect(() => {
    if (open && galleryId) {
      findDuplicates();
    }
  }, [open, galleryId]);

  const findDuplicates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/galleries/${galleryId}/duplicates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDuplicates(response.data.duplicates);
      setTotalDuplicatePhotos(response.data.total_duplicate_photos);
      
      if (response.data.duplicate_count === 0) {
        toast.success('No duplicate filenames found!');
      }
    } catch (error) {
      console.error('Error finding duplicates:', error);
      toast.error('Failed to find duplicates');
    } finally {
      setLoading(false);
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(photoId)) {
        newSet.delete(photoId);
      } else {
        newSet.add(photoId);
      }
      return newSet;
    });
  };

  const selectAllButOne = (filename) => {
    const photos = duplicates[filename];
    if (!photos || photos.length < 2) return;
    
    // Keep the first photo (oldest), select the rest for deletion
    setSelectedPhotos(prev => {
      const newSet = new Set(prev);
      photos.slice(1).forEach(photo => newSet.add(photo.id));
      return newSet;
    });
  };

  const selectAllDuplicates = () => {
    // For all duplicate groups, keep the oldest (first) and select rest for deletion
    const photosToDelete = new Set();
    Object.values(duplicates).forEach(photos => {
      if (photos.length > 1) {
        // Skip the first (oldest), add the rest
        photos.slice(1).forEach(photo => photosToDelete.add(photo.id));
      }
    });
    setSelectedPhotos(photosToDelete);
  };

  const deleteAllDuplicates = async () => {
    // Auto-select all duplicates except oldest
    const photosToDelete = new Set();
    Object.values(duplicates).forEach(photos => {
      if (photos.length > 1) {
        photos.slice(1).forEach(photo => photosToDelete.add(photo.id));
      }
    });

    if (photosToDelete.size === 0) {
      toast.error('No duplicates to delete');
      return;
    }

    const confirmDelete = window.confirm(
      `This will delete ${photosToDelete.size} duplicate photo(s) and keep the oldest copy of each. Continue?`
    );

    if (!confirmDelete) return;

    setRemoving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${BACKEND_URL}/api/galleries/${galleryId}/photos/duplicates`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { photo_ids: Array.from(photosToDelete) }
        }
      );

      toast.success(`Successfully removed ${response.data.deleted_count} duplicate photos`);
      
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn('Some deletions failed:', response.data.errors);
      }

      // Notify parent to refresh gallery
      if (onPhotosRemoved) {
        onPhotosRemoved(response.data.deleted_count);
      }

      // Refresh duplicates list
      await findDuplicates();
      setSelectedPhotos(new Set());
      
    } catch (error) {
      console.error('Error removing duplicates:', error);
      toast.error('Failed to remove duplicates');
    } finally {
      setRemoving(false);
    }
  };

  const removeDuplicates = async () => {
    if (selectedPhotos.size === 0) {
      toast.error('Please select photos to remove');
      return;
    }

    setRemoving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${BACKEND_URL}/api/galleries/${galleryId}/photos/duplicates`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { photo_ids: Array.from(selectedPhotos) }
        }
      );

      toast.success(`Successfully removed ${response.data.deleted_count} duplicate photos`);
      
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn('Some deletions failed:', response.data.errors);
      }

      // Notify parent to refresh gallery
      if (onPhotosRemoved) {
        onPhotosRemoved(response.data.deleted_count);
      }

      // Refresh duplicates list
      await findDuplicates();
      setSelectedPhotos(new Set());
      
    } catch (error) {
      console.error('Error removing duplicates:', error);
      toast.error('Failed to remove duplicates');
    } finally {
      setRemoving(false);
    }
  };

  const duplicateEntries = Object.entries(duplicates);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <span>Duplicate Filenames</span>
          </DialogTitle>
          <DialogDescription>
            {loading ? (
              'Scanning for duplicate filenames...'
            ) : duplicateEntries.length === 0 ? (
              <span className="text-green-600 font-medium flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4" />
                <span>No duplicate filenames found in this gallery</span>
              </span>
            ) : (
              `Found ${duplicateEntries.length} duplicate filename(s) affecting ${totalDuplicatePhotos} photos`
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : duplicateEntries.length > 0 ? (
          <div className="space-y-6">
            {duplicateEntries.map(([filename, photos]) => (
              <div key={filename} className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900">
                    {filename}
                    <span className="ml-2 text-sm text-slate-600">
                      ({photos.length} copies)
                    </span>
                  </h3>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => selectAllButOne(filename)}
                    disabled={removing}
                  >
                    Select all but oldest
                  </Button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {photos.map((photo, index) => (
                    <div
                      key={photo.id}
                      className={`relative border-2 rounded-lg overflow-hidden ${
                        selectedPhotos.has(photo.id)
                          ? 'border-red-500 bg-red-50'
                          : 'border-slate-200'
                      }`}
                    >
                      <div className="aspect-square relative">
                        <img
                          src={photo.thumbnail_url}
                          alt={photo.filename}
                          className="w-full h-full object-cover"
                        />
                        
                        {/* Checkbox overlay */}
                        <div
                          className="absolute top-2 left-2 bg-white rounded-md shadow-md p-1 cursor-pointer"
                          onClick={() => togglePhotoSelection(photo.id)}
                        >
                          <Checkbox
                            checked={selectedPhotos.has(photo.id)}
                            onCheckedChange={() => togglePhotoSelection(photo.id)}
                          />
                        </div>

                        {/* Badge for oldest */}
                        {index === 0 && (
                          <div className="absolute top-2 right-2 bg-green-600 text-white text-xs px-2 py-1 rounded-md">
                            Oldest
                          </div>
                        )}
                      </div>
                      
                      <div className="p-2 bg-white">
                        <p className="text-xs text-slate-600 truncate">
                          {new Date(photo.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        <DialogFooter>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between w-full gap-3">
            <p className="text-sm text-slate-600">
              {selectedPhotos.size > 0 ? (
                <span className="font-medium text-orange-600">
                  {selectedPhotos.size} photo(s) selected for removal
                </span>
              ) : duplicateEntries.length > 0 && (
                <span className="text-slate-500">
                  Tip: Use "Delete All Duplicates" to remove all at once
                </span>
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={onClose} disabled={removing}>
                Close
              </Button>
              {duplicateEntries.length > 0 && (
                <>
                  <Button
                    onClick={selectAllDuplicates}
                    disabled={removing}
                    variant="outline"
                    className="border-orange-500 text-orange-700 hover:bg-orange-50"
                  >
                    Select All Duplicates
                  </Button>
                  <Button
                    onClick={deleteAllDuplicates}
                    disabled={removing}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {removing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Removing...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete All Duplicates (Keep Oldest)
                      </>
                    )}
                  </Button>
                  {selectedPhotos.size > 0 && (
                    <Button
                      onClick={removeDuplicates}
                      disabled={removing}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      {removing ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Removing...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove Selected ({selectedPhotos.size})
                        </>
                      )}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicatePhotosDialog;
